import { useRef, useState } from "react";
import { useCommunity } from "../../context/CommunityContext";
import useAuth from "../../hooks/useAuth";
import { cn } from "../../lib/utils";
import ImageCropModal from "../user/ImageCropModal";

export default function CommunityBanner({
  // title = "Developer Community",
  // description = "A place for developers to learn, share, and grow together",
  // bannerImage = "/placeholder.svg?height=300&width=1200",
  // logoImage = "/placeholder.svg?height=80&width=80",
  className,
}) {
  const communityCtx = useCommunity();
  console.log(communityCtx.community)
  const title = communityCtx.community.community.name;
  const description = communityCtx.community.community.description;
  const bannerImage = communityCtx.community.community.coverPicture;
  const logoImage = communityCtx.community.community.profilePicture;

  const { isAuth } = useAuth();
  const profileImageInputRef = useRef();
  const coverImageInputRef = useRef();
  const [imageError, setImageError] = useState('');

  // Image cropping states
  const [selectedImage, setSelectedImage] = useState(null);
  const [showProfileCropModal, setShowProfileCropModal] = useState(false);
  const [showCoverCropModal, setShowCoverCropModal] = useState(false);


  // Validate image before upload
  const validateImage = (file, maxSize) => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'File type not supported. Please use JPEG, PNG, or WebP format.';
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return `File size too large. Maximum size is ${maxSizeMB}MB.`;
    }

    return '';
  };


  // Handle profile image selection
  const handleProfileImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImageError('');

    // Validate image (max 2MB)
    const validationError = validateImage(file, 2 * 1024 * 1024);
    if (validationError) {
      setImageError(validationError);
      return;
    }

    // Create a URL for the selected image
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowProfileCropModal(true);
  };

  // Handle cover image selection
  const handleCoverImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImageError('');

    // Validate image (max 5MB)
    const validationError = validateImage(file, 5 * 1024 * 1024);
    if (validationError) {
      setImageError(validationError);
      return;
    }

    // Create a URL for the selected image
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowCoverCropModal(true);
  };

  // Handle saving the cropped profile image
  const handleSaveProfileImage = async (croppedBlob) => {
    try {
      // Create a file from the blob
      const file = new File([croppedBlob], 'profile-image.jpeg', { type: 'image/jpeg' });

      // Upload the cropped image
      await communityCtx.uploadProfileImage(file);
    } catch (err) {
      console.error('Failed to upload profile picture:', err);
      setImageError('Failed to upload profile picture. Please try again.');
    } finally {
      // Clean up the object URL
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
        setSelectedImage(null);
      }
    }
  };

  // Handle saving the cropped cover image
  const handleSaveCoverImage = async (croppedBlob) => {
    try {
      // Create a file from the blob
      const file = new File([croppedBlob], 'cover-image.jpeg', { type: 'image/jpeg' });

      // Upload the cropped image
      await communityCtx.uploadCoverImage(file);
    } catch (err) {
      console.error('Failed to upload cover picture:', err);
      setImageError('Failed to upload cover picture. Please try again.');
    } finally {
      // Clean up the object URL
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
        setSelectedImage(null);
      }
    }
  };

  // Close crop modal
  const handleCloseCropModal = () => {
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
      setSelectedImage(null);
    }
    setShowProfileCropModal(false);
    setShowCoverCropModal(false);
  };


  return (
    <div className={cn("relative rounded-xl overflow-hidden mb-6", className)}>
      {/* Cover image */}
      <div className="relative h-[200px] sm:h-[220px] md:h-[240px] bg-gray-200">
        <img
          src={bannerImage || "/placeholder.svg"}
          alt="Community Cover"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

        {/* Cover image upload progress */}
        {communityCtx.uploadProgress?.coverImage > 0 && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white">
            <div className="text-center">
              <div className="mb-2">Uploading...</div>
              <div className="w-48 bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${communityCtx.uploadProgress.coverImage}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Cover edit button (if owner) */}
        {communityCtx.isOwner && (
          <div className="absolute top-4 right-4">
            <input
              type="file"
              ref={coverImageInputRef}
              onChange={handleCoverImageChange}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />
            <button
              onClick={() => coverImageInputRef.current.click()}
              className="bg-white bg-opacity-80 hover:bg-opacity-100 px-3 py-1 rounded text-sm font-medium"
              disabled={communityCtx.uploadProgress.coverImage > 0}
            >
              Edit Cover
            </button>
          </div>
        )}
      </div>

      {/* Logo and details */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
        <div className="flex items-center gap-4">
          <div className="relative group">
            <img
              src={logoImage || "/placeholder.svg"}
              alt="Community Logo"
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl border-4 border-white shadow-lg object-cover"
            />

            {/* Profile image progress */}
            {communityCtx.uploadProgress?.profileImage > 0 && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center text-white">
                <div className="w-12 bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full"
                    style={{ width: `${communityCtx.uploadProgress.profileImage}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Profile image edit overlay */}
            {communityCtx.isOwner && (
              <>
                <input
                  type="file"
                  ref={profileImageInputRef}
                  onChange={handleProfileImageChange}
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                />
                <div
                  className={`absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center cursor-pointer transition ${
                  communityCtx.loading2.profilePicture || communityCtx.loading2.coverPicture 
                    ? 'opacity-100 cursor-not-allowed' 
                    : 'opacity-0 group-hover:opacity-100 cursor-pointer'
                } transition-opacity`}
                  onClick={() => profileImageInputRef.current.click()}
                >
                  <svg
                    className="w-6 h-6 text-white opacity-0 group-hover:opacity-100"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536M9 11l2 2 7-7M3 17v4h4l10.293-10.293a1 1 0 000-1.414l-3.586-3.586a1 1 0 00-1.414 0L3 17z"
                    />
                  </svg>
                </div>
              </>
            )}
          </div>
          <div className="text-white">
            <h1 className="text-xl sm:text-2xl font-bold">{title}</h1>
            <p className="text-sm sm:text-base text-gray-200 max-w-2xl line-clamp-2">{description}</p>
          </div>
        </div>
      </div>
      {/* Cover Image Crop Modal */}
      {showCoverCropModal && selectedImage && (
        <ImageCropModal
          isOpen={showCoverCropModal}
          onClose={handleCloseCropModal}
          image={selectedImage}
          onSave={handleSaveCoverImage}
          title="Edit Cover Picture"
          aspectRatio={{ width: 3, height: 1 }}
          recommendedSize={{ width: 1200, height: 400 }}
        />
      )}

      {/* Image Crop Modals */}
      {/* Profile Image Crop Modal */}
      {showProfileCropModal && selectedImage && (
        <ImageCropModal
          isOpen={showProfileCropModal}
          onClose={handleCloseCropModal}
          image={selectedImage}
          onSave={handleSaveProfileImage}
          title="Edit Profile Picture"
          aspectRatio={{ width: 1, height: 1 }}
          recommendedSize={{ width: 400, height: 400 }}
        />
      )}
    </div>

  )

  return (
    <div className={cn("relative rounded-xl overflow-hidden mb-6", className)}>
      {/* Banner image with gradient overlay */}
      <div className="relative h-[200px] sm:h-[220px] md:h-[240px]">
        <img src={bannerImage || "/placeholder.svg"} alt="Community Banner" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
      </div>

      {/* Content positioned at the bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
        <div className="flex items-center gap-4">
          <img
            src={logoImage || "/placeholder.svg"}
            alt="Community Logo"
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl border-4 border-white shadow-lg"
          />
          <div className="text-white">
            <h1 className="text-xl sm:text-2xl font-bold">{title}</h1>
            <p className="text-sm sm:text-base text-gray-200 max-w-2xl line-clamp-2">{description}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

